import { Component, effect, OnInit, DestroyRef, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { SettingsService } from 'src/app/shared/services/settings';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import * as _ from 'lodash';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CanComponentDeactivate } from 'src/app/guards/can-deactivate.guard';
import { LtmsLoaderComponent } from 'src/app/shared/components/ltms-loader/ltms-loader.component';

// Declare Helpers globally
declare const Helpers: any;

const DEFAULT_ACTION_SUMMARY_COLOR_CONFIG = {
  count: {
    green_bucket: { label: 'Solved', color: '#10a754' },
    red_bucket: { label: 'Unsolved', color: '#ed2424' }
  },
  ageing: {
    green_bucket: { label: 'Unsolved Within Target TAT', color: '#10a754' },
    red_bucket: { label: 'Unsolved Outside Of Target TAT', color: '#69a8fc' }
  },
  tat: {
    green_bucket: { label: 'Solved Within Target TAT', color: '#10a754' },
    red_bucket: { label: 'Solved Outside of Target TAT', color: '#fab416' }
  }
};

@Component({
  selector: 'app-litmus-action-summary',
  templateUrl: './litmus-action-summary.component.html',
  styleUrls: ['./litmus-action-summary.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, LtmsLoaderComponent]
})
export class LitmusActionSummaryComponent implements OnInit, OnChanges, CanComponentDeactivate {
  @Input() readonly: boolean = true;

  destroyRef = inject(DestroyRef);
  summaryForm!: FormGroup;
  selectedTab = '';
  isSaving = false;
  isCancelling = false;
  isSaveEnabled = false;
  originalConfiguration: any = {};
  configuration: any = {};
  brandDetails: any = {};
  managerDetails: any = {};
  configurationLoaded: boolean = false;
  private initializedBrandId: string | null = null;
  isLessThanADay: boolean = false;
  lastActionableAddedBy: string = '';
  lastActionableAddedOn: string = '';
  hasPrimaryRating: boolean = false;
  availableRatingValues: number[] = [];
  actionablesHistory: any[] = [];
  fromRatingMin: number | null = null;
  fromRatingMax: number | null = null;
  toRatingMin: number | null = null;
  toRatingMax: number | null = null;
  actionSummaryWidgetSettings: any = _.cloneDeep(DEFAULT_ACTION_SUMMARY_COLOR_CONFIG);

  constructor(
    private fb: FormBuilder,
    private _toast: ToastService,
    private stateService: AppStateService,
    private _settingsService: SettingsService,
    private accountsService: AccountsService
  ) {
    effect(() => {
      if (this.stateService.initialized()) {
        const brandDetail: any = this.stateService.brandDetails();
        const managerDetails: any = this.stateService.managerAccess();
        if (!_.isEmpty(brandDetail)) {
          this.brandDetails = brandDetail;
          this.configurationLoaded = true;
          this.retrieveActionSummaryConfig(brandDetail);
          if (this.summaryForm && this.initializedBrandId === brandDetail._id) {
            this.applyBrandDetailsToForm();
          } else {
            this.initializedBrandId = brandDetail._id;
            this.initializeForm();
          }
        }

        if (!_.isEmpty(managerDetails)) {
          this.managerDetails = managerDetails;
        }
      }
    })
  }

  ngOnInit(): void {
    this.originalConfiguration = { ...this.configuration };
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readonly']) {
      this.updateFormDisabledState();
    }
  }

  get isToRatingDisabled(): boolean {
    const fromRatingValue = this.summaryForm?.get('actionableFromRating')?.value;
    return (!fromRatingValue && fromRatingValue !== 0);
  }

  private initializeForm(): void {
    if (this.configurationLoaded && !_.isEmpty(this.brandDetails)) {
      this.configuration = this.extractConfigurationFromBrandDetails(this.brandDetails);
      this.originalConfiguration = { ...this.configuration };
    }

    this.summaryForm = this.fb.group({
      actionableFromRating: [''],
      actionableToRating: [''],
      targetTAT: [this.configuration?.targetTAT || 1],
      greenBucketLabel: [''],
      greenBucketColor: ['#10a754'],
      redBucketLabel: [''],
      redBucketColor: ['#ed2424']
    });

    this.updateFormDisabledState();

    this.summaryForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.onFormChange();
      });
  }

  private applyBrandDetailsToForm(): void {
    if (!this.summaryForm) {
      return;
    }
    this.configuration = this.extractConfigurationFromBrandDetails(this.brandDetails);
    if (!this.isSaveEnabled) {
      this.originalConfiguration = { ...this.configuration };
      this.summaryForm.patchValue({
        targetTAT: this.configuration?.targetTAT || 1
      }, { emitEvent: false });
      this.isSaveEnabled = false;
    }
    this.updateFormDisabledState();
  }

  onFormChange(): void {
    this.isSaveEnabled = this.hasFormChanged();
  }

  /**
   * Retrieve action summary config following action-config-component pattern
   */
  private retrieveActionSummaryConfig(brand: any): void {
    if (!_.isEmpty(brand)) {
      this.brandDetails = _.cloneDeep(brand);
    }

    this.hasPrimaryRating = false;
    this.availableRatingValues = [];
    this.actionablesHistory = [];
    this.isLessThanADay = false;
    this.lastActionableAddedBy = '';
    this.lastActionableAddedOn = '';

    const questions = _.get(brand, 'detailed_feedback_items', []);
    if (questions && questions.length) {
      _.each(questions, (q: any) => {
        if (q.is_primary_rating || q.field_id === 'primary_rating') {
          this.hasPrimaryRating = true;
          const possibleValues = q.possible_values;
          if (possibleValues && !_.isEmpty(possibleValues)) {
            const values = (_.isArray(possibleValues) ? possibleValues : _.values(possibleValues))
              .map((v: any) => Number(v))
              .filter((v: number) => !isNaN(v));
            this.availableRatingValues = _.clone(values);
            const minMax = this.getMinMaxValues(values);
            this.fromRatingMin = minMax.minVal;
            this.fromRatingMax = minMax.maxVal;
          }
        }
      });
    }

    const colorConfig = _.get(brand, 'action_management_configuration.widget_settings.action_summary');
    const actionConfig = _.get(brand, 'action_management_configuration');
    if (actionConfig && !_.isEmpty(actionConfig) && !(colorConfig && !_.isEmpty(colorConfig))) {
      _.set(this.brandDetails, 'action_management_configuration.widget_settings.action_summary',
        _.cloneDeep(DEFAULT_ACTION_SUMMARY_COLOR_CONFIG));
    }

    this.actionSummaryWidgetSettings = _.cloneDeep(
      _.get(this.brandDetails, 'action_management_configuration.widget_settings.action_summary', DEFAULT_ACTION_SUMMARY_COLOR_CONFIG)
    );

    const actionables = _.get(brand, 'action_management_configuration.actionables', []);
    if (actionables && actionables.length) {
      const lastActionable = actionables[actionables.length - 1];
      const today = new Date();
      const lastActionableAddedOn = lastActionable.added_on ? new Date(lastActionable.added_on) : null;

      if (lastActionableAddedOn && (today.getTime() - lastActionableAddedOn.getTime()) < 86400000) {
        this.isLessThanADay = true;
      }

      if (lastActionableAddedOn) {
        this.lastActionableAddedOn = this.formatDateTime(lastActionableAddedOn);
      }
      this.lastActionableAddedBy = lastActionable.added_by || '';

      _.each(actionables, (actionable: any) => {
        const obj: any = {};
        if (actionable.primary_rating && actionable.primary_rating.length) {
          const ratings = actionable.primary_rating.map((n: number) => Number(n));
          obj.min = Math.min(...ratings);
          obj.max = Math.max(...ratings);
        }
        if (actionable.date && actionable.date.start) {
          obj.start = this.formatDate(new Date(actionable.date.start));
        }
        if (actionable.date && actionable.date.end) {
          obj.end = this.formatDate(new Date(actionable.date.end));
        } else {
          obj.end = 'present';
        }
        this.actionablesHistory.push(obj);
      });
    }
  }

  private getMinMaxValues(values: number[], min?: number | null): { minVal: number; maxVal: number } {
    if (!values?.length) {
      return { minVal: 0, maxVal: 0 };
    }

    // When a From value is provided, To must use only ratings strictly greater than From
    if (min || min === 0) {
      const greaterValues = values.filter((val) => val > (min as number));
      if (!greaterValues.length) {
        return { minVal: min as number, maxVal: min as number };
      }
      return {
        minVal: Math.min(...greaterValues),
        maxVal: Math.max(...greaterValues)
      };
    }

    return {
      minVal: Math.min(...values),
      maxVal: Math.max(...values)
    };
  }

  private clampRating(value: number, min: number | null, max: number | null): number {
    let result = value;
    if (min !== null && !isNaN(min) && result < min) {
      result = min;
    }
    if (max !== null && !isNaN(max) && result > max) {
      result = max;
    }
    return result;
  }

  private isEmptyRating(value: any): boolean {
    return value === '' || value === null || value === undefined;
  }

  private toWholeNumber(value: any): number | null {
    if (this.isEmptyRating(value)) {
      return null;
    }
    const numeric = Number(value);
    if (isNaN(numeric)) {
      return null;
    }
    return Math.trunc(numeric);
  }

  onWholeNumberInput(which: 'from' | 'to', input: HTMLInputElement): void {
    const raw = input.value;
    if (raw === '' || raw === '-' || raw === '+') {
      return;
    }

    const whole = this.toWholeNumber(raw);
    if (whole === null) {
      input.value = raw.startsWith('-') ? '-' : '';
      return;
    }

    const bounds = which === 'from'
      ? { min: this.fromRatingMin, max: this.fromRatingMax }
      : { min: this.toRatingMin, max: this.toRatingMax };
    const limited = this.limitToRange(whole, bounds.min, bounds.max);
    const unchangedInteger = limited === whole && /^-?\d+$/.test(raw);
    if (unchangedInteger) {
      return;
    }

    input.value = String(limited);
    if (which === 'from') {
      this.onActionableFromChange(limited, input);
    } else {
      this.onActionableToChange(limited, input);
    }
  }

  private limitToRange(value: number, min: number | null, max: number | null): number {
    if (max !== null && !isNaN(max) && value > max) {
      return Math.trunc(max);
    }
    if (min !== null && !isNaN(min) && value < min && !this.canStillReachRange(value, min, max)) {
      return Math.trunc(min);
    }
    return value;
  }

  private canStillReachRange(value: number, min: number, max: number | null): boolean {
    if (value < 0) {
      return false;
    }
    const ceiling = max !== null && !isNaN(max) ? max : Number.POSITIVE_INFINITY;
    const withOneMoreDigitMin = value * 10;
    const withOneMoreDigitMax = value * 10 + 9;
    return withOneMoreDigitMin <= ceiling && withOneMoreDigitMax >= min;
  }

  private formatDate(date: Date): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(date.getDate()).padStart(2, '0')} ${months[date.getMonth()]} ${date.getFullYear()}`;
  }

  private formatDateTime(date: Date): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${String(date.getDate()).padStart(2, '0')}-${months[date.getMonth()]}-${date.getFullYear()} ${hours}:${minutes}`;
  }

  private updateFormDisabledState(): void {
    if (!this.summaryForm) return;

    if (this.readonly) {
      this.summaryForm.disable({ emitEvent: false });
      return;
    }

    this.summaryForm.enable({ emitEvent: false });

    if (this.isLessThanADay) {
      this.summaryForm.get('actionableFromRating')?.disable({ emitEvent: false });
      this.summaryForm.get('actionableToRating')?.disable({ emitEvent: false });
    } else {
      this.summaryForm.get('actionableFromRating')?.enable({ emitEvent: false });
      if (this.isToRatingDisabled) {
        this.summaryForm.get('actionableToRating')?.disable({ emitEvent: false });
      } else {
        this.summaryForm.get('actionableToRating')?.enable({ emitEvent: false });
      }
    }

    if (this.selectedTab === 'count') {
      this.summaryForm.get('greenBucketLabel')?.disable({ emitEvent: false });
      this.summaryForm.get('redBucketLabel')?.disable({ emitEvent: false });
    } else if (this.selectedTab) {
      this.summaryForm.get('greenBucketLabel')?.enable({ emitEvent: false });
      this.summaryForm.get('redBucketLabel')?.enable({ emitEvent: false });
    }
  }

  onActionableFromChange(from: any, inputEl?: HTMLInputElement): void {
    if (this.isEmptyRating(from)) {
      this.summaryForm.patchValue({ actionableFromRating: '', actionableToRating: '' }, { emitEvent: false });
      this.toRatingMin = null;
      this.toRatingMax = null;
      this.updateFormDisabledState();
      this.isSaveEnabled = this.hasFormChanged();
      return;
    }

    const wholeFrom = this.toWholeNumber(from);
    if (wholeFrom === null) {
      this.summaryForm.patchValue({ actionableFromRating: '', actionableToRating: '' }, { emitEvent: false });
      this.toRatingMin = null;
      this.toRatingMax = null;
      this.updateFormDisabledState();
      this.isSaveEnabled = this.hasFormChanged();
      return;
    }

    // From must stay within overall min/max rating bounds and stay a whole number
    let fromValue = this.clampRating(wholeFrom, this.fromRatingMin, this.fromRatingMax);

    // Keep room for a To rating that is always greater than From
    if (this.availableRatingValues.length) {
      const greaterValues = this.availableRatingValues.filter((val) => val > fromValue);
      if (!greaterValues.length && this.fromRatingMax !== null && fromValue >= this.fromRatingMax) {
        const nextLower = this.availableRatingValues
          .filter((val) => val < this.fromRatingMax!)
          .sort((a, b) => b - a)[0];
        if (nextLower || nextLower === 0) {
          fromValue = Math.trunc(nextLower);
        }
      }
    }
    fromValue = Math.trunc(fromValue);

    const previousFrom = this.summaryForm.get('actionableFromRating')?.value;
    const fromChanged = this.isEmptyRating(previousFrom) || Number(previousFrom) !== fromValue;

    this.summaryForm.patchValue({
      actionableFromRating: fromValue,
      ...(fromChanged ? { actionableToRating: '' } : {})
    }, { emitEvent: false });

    if (this.availableRatingValues.length) {
      const toMinMax = this.getMinMaxValues(this.availableRatingValues, fromValue);
      this.toRatingMin = toMinMax.minVal;
      this.toRatingMax = toMinMax.maxVal;
    } else {
      this.toRatingMin = null;
      this.toRatingMax = null;
    }

    // If To still has a value, keep it only when it remains valid for the new From
    const currentTo = this.summaryForm.get('actionableToRating')?.value;
    if (!this.isEmptyRating(currentTo) || currentTo === 0) {
      const toValue = Number(currentTo);
      if (
        isNaN(toValue) ||
        toValue <= fromValue ||
        (this.toRatingMin !== null && toValue < this.toRatingMin) ||
        (this.toRatingMax !== null && toValue > this.toRatingMax)
      ) {
        this.summaryForm.patchValue({ actionableToRating: '' }, { emitEvent: false });
      }
    }

    this.writeInputValue(inputEl, fromValue);
    this.updateFormDisabledState();
    this.isSaveEnabled = this.hasFormChanged();
  }

  onActionableToChange(to: any, inputEl?: HTMLInputElement): void {
    if (this.isEmptyRating(to)) {
      this.summaryForm.patchValue({ actionableToRating: '' }, { emitEvent: false });
      this.isSaveEnabled = this.hasFormChanged();
      return;
    }

    const wholeTo = this.toWholeNumber(to);
    if (wholeTo === null) {
      this.summaryForm.patchValue({ actionableToRating: '' }, { emitEvent: false });
      this.isSaveEnabled = this.hasFormChanged();
      return;
    }

    // To must stay within allowed min/max (values greater than From) and stay a whole number
    let toValue = this.clampRating(wholeTo, this.toRatingMin, this.toRatingMax);

    const fromValue = this.summaryForm.get('actionableFromRating')?.value;
    if (!this.isEmptyRating(fromValue) && toValue <= Number(fromValue)) {
      toValue = this.toRatingMin !== null ? this.toRatingMin : Number(fromValue) + 1;
      toValue = this.clampRating(toValue, this.toRatingMin, this.toRatingMax);
    }
    toValue = Math.trunc(toValue);

    this.summaryForm.patchValue({ actionableToRating: toValue }, { emitEvent: false });
    this.writeInputValue(inputEl, toValue);
    this.isSaveEnabled = this.hasFormChanged();
  }

  private writeInputValue(inputEl: HTMLInputElement | undefined, value: number | ''): void {
    if (!inputEl) {
      return;
    }
    inputEl.value = value === '' ? '' : String(value);
  }

  onTabChange(tab: string): void {
    this.selectedTab = tab;
    this.patchBucketFieldsFromSelectedTab();
    this.updateFormDisabledState();
    this.isSaveEnabled = this.hasFormChanged();
  }

  private patchBucketFieldsFromSelectedTab(): void {
    if (!this.selectedTab || !this.summaryForm) return;

    const tabConfig = _.get(this.actionSummaryWidgetSettings, this.selectedTab, {});
    this.summaryForm.patchValue({
      greenBucketLabel: _.get(tabConfig, 'green_bucket.label', ''),
      greenBucketColor: _.get(tabConfig, 'green_bucket.color', '#10a754'),
      redBucketLabel: _.get(tabConfig, 'red_bucket.label', ''),
      redBucketColor: _.get(tabConfig, 'red_bucket.color', '#ed2424')
    }, { emitEvent: false });
  }

  onBucketFieldChange(field: string, value: string): void {
    this.summaryForm.patchValue({ [field]: value }, { emitEvent: false });

    if (this.selectedTab) {
      if (field === 'greenBucketLabel') {
        _.set(this.actionSummaryWidgetSettings, `${this.selectedTab}.green_bucket.label`, value);
      } else if (field === 'greenBucketColor') {
        _.set(this.actionSummaryWidgetSettings, `${this.selectedTab}.green_bucket.color`, value);
      } else if (field === 'redBucketLabel') {
        _.set(this.actionSummaryWidgetSettings, `${this.selectedTab}.red_bucket.label`, value);
      } else if (field === 'redBucketColor') {
        _.set(this.actionSummaryWidgetSettings, `${this.selectedTab}.red_bucket.color`, value);
      }
    }

    this.isSaveEnabled = this.hasFormChanged();
  }

  private hasFormChanged(): boolean {
    if (!this.summaryForm) return false;

    const currentValues = this.summaryForm.getRawValue();
    const actionableChanged = (
      (currentValues.actionableFromRating !== '' && currentValues.actionableFromRating !== null && currentValues.actionableFromRating !== undefined) ||
      (currentValues.actionableToRating !== '' && currentValues.actionableToRating !== null && currentValues.actionableToRating !== undefined)
    );

    const targetTatChanged = Number(currentValues.targetTAT) !== Number(this.originalConfiguration?.targetTAT || 1);
    const widgetChanged = !_.isEqual(
      this.actionSummaryWidgetSettings,
      _.get(this.originalConfiguration, 'widgetSettings', DEFAULT_ACTION_SUMMARY_COLOR_CONFIG)
    );

    return actionableChanged || targetTatChanged || widgetChanged;
  }

  private validateActionSummaryConfig(formValues: any): { isValid: boolean; errorMsg: string } {
    const validationDetails = {
      isValid: true,
      errorMsg: ''
    };

    const fromRating = formValues.actionableFromRating;
    const toRating = formValues.actionableToRating;
    const hasFrom = !this.isEmptyRating(fromRating) || fromRating === 0;
    const hasTo = !this.isEmptyRating(toRating) || toRating === 0;

    if (hasFrom && !hasTo) {
      validationDetails.isValid = false;
      validationDetails.errorMsg = '<b>Actionables range</b> incorrectly defined. Please recheck.';
      return validationDetails;
    }

    if (hasFrom && hasTo) {
      const fromValue = this.toWholeNumber(fromRating);
      const toValue = this.toWholeNumber(toRating);

      if (
        fromValue === null ||
        toValue === null ||
        !Number.isInteger(fromValue) ||
        !Number.isInteger(toValue) ||
        (this.fromRatingMin !== null && fromValue < this.fromRatingMin) ||
        (this.fromRatingMax !== null && fromValue > this.fromRatingMax) ||
        (this.toRatingMin !== null && toValue < this.toRatingMin) ||
        (this.toRatingMax !== null && toValue > this.toRatingMax) ||
        toValue <= fromValue
      ) {
        validationDetails.isValid = false;
        validationDetails.errorMsg = '<b>Actionables range</b> incorrectly defined. Please recheck.';
        return validationDetails;
      }
    }

    const targetTAT = Number(formValues.targetTAT);
    if (formValues.targetTAT === '' || formValues.targetTAT === null || formValues.targetTAT === undefined || isNaN(targetTAT) || targetTAT <= 0) {
      validationDetails.isValid = false;
      validationDetails.errorMsg = '<b>Target TAT</b> incorrectly defined. Please recheck.';
      return validationDetails;
    }

    if (this.selectedTab) {
      const selectedTabConfig = _.get(this.actionSummaryWidgetSettings, this.selectedTab, {});
      if (!(selectedTabConfig['red_bucket'] && selectedTabConfig['red_bucket']['label'])) {
        validationDetails.isValid = false;
        validationDetails.errorMsg = '<b>Legends</b> incorrectly defined. Please recheck.';
      } else if (!(selectedTabConfig['green_bucket'] && selectedTabConfig['green_bucket']['label'])) {
        validationDetails.isValid = false;
        validationDetails.errorMsg = '<b>Legends</b> incorrectly defined. Please recheck';
      }
    }

    return validationDetails;
  }

  private getCurrentUser(): string {
    return this.managerDetails?.manager?.email_address
      || this.managerDetails?.manager?.name
      || this.managerDetails?.manager?._id
      || '';
  }

  saveConfiguration() {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (this.isSaving) return;

    this.isSaving = true;
    const formValues = this.summaryForm.getRawValue();

    // Prepare actionables if range is specified (following reference pattern)
    const brandActionManagementConfig = _.cloneDeep(
      _.get(this.brandDetails, 'action_management_configuration', {})
    );

    if ((formValues.actionableFromRating || formValues.actionableFromRating === 0) &&
      (formValues.actionableToRating || formValues.actionableToRating === 0)) {

      const primary_rating: number[] = [];
      const fromRating = this.toWholeNumber(formValues.actionableFromRating);
      const toRating = this.toWholeNumber(formValues.actionableToRating);
      if (fromRating !== null && toRating !== null) {
        for (let num = fromRating; num <= toRating; num++) {
          primary_rating.push(num);
        }
      }

      let actionables = _.get(brandActionManagementConfig, 'actionables', []);
      actionables = actionables && actionables.length ? _.cloneDeep(actionables) : [];

      const newActionable: any = { date: {} };
      const lastActionable = actionables.pop();
      let now: any = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
      const day = 60 * 60 * 24 * 1000;
      now = new Date(now.getTime() + day);

      if (lastActionable) {
        lastActionable.date = lastActionable.date || {};
        lastActionable.date.end = now.toISOString();
        newActionable.date.start = now.toISOString();
        newActionable.primary_rating = primary_rating;
        newActionable.added_by = this.getCurrentUser();
        newActionable.added_on = new Date();
        actionables.push(lastActionable);
      } else {
        newActionable.date.start = this.brandDetails.created_date;
        newActionable.primary_rating = primary_rating;
        newActionable.added_by = this.getCurrentUser();
        newActionable.added_on = new Date();
      }

      actionables.push(newActionable);
      brandActionManagementConfig.actionables = actionables;
    }

    const targetTAT = Number(formValues.targetTAT);
    _.set(brandActionManagementConfig, 'ageing_config.turnaround_time', isNaN(targetTAT) || targetTAT <= 0 ? 1 : targetTAT);
    if(this.hasPrimaryRating){
      _.set(brandActionManagementConfig, 'widget_settings.action_summary', _.cloneDeep(this.actionSummaryWidgetSettings));
    }
   

    const validationResult = this.validateActionSummaryConfig(formValues);
    if (!validationResult.isValid) {
      if (Helpers && Helpers.smallAlertBox) {
        Helpers.smallAlertBox('Error', validationResult.errorMsg, 'error');
      }
      this.isSaving = false;
      return;
    }
    if(this.managerDetails?.manager?._id){
      brandActionManagementConfig.updated_by = this.managerDetails?.manager?._id;
      brandActionManagementConfig.updated_on = new Date().toISOString();
    }
    const actionSummarySettingConfig: any = {
      action_management_configuration: brandActionManagementConfig
    };

    this._settingsService.updateBrandDashboardConfiguration(this.brandDetails._id, actionSummarySettingConfig)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response && response.code == 0) {
            if (Helpers && Helpers.smallAlertBox) {
              Helpers.smallAlertBox('Success', 'Action configuration has been updated successfully.', 'success', 3500);
            }

            this.summaryForm.patchValue({
              actionableFromRating: '',
              actionableToRating: ''
            }, { emitEvent: false });
            this.selectedTab = '';
            this.isSaveEnabled = false;
            this.afterUpdateBrandConfig();
          } else {
            if (Helpers && Helpers.smallAlertBox) {
              Helpers.smallAlertBox('Error', 'Error while updating Summary settings.', 'error', 3500);
            }
          }
          this.isSaving = false;
        },
        error: (error: any) => {
          console.error('Error while updating Summary settings:', error);
          if (Helpers && Helpers.smallAlertBox) {
            Helpers.smallAlertBox('Error', 'Error while updating Summary settings.', 'error', 3500);
          }
          this.isSaving = false;
        }
      });
  }

  afterUpdateBrandConfig() {
    this.accountsService.getBrandDetail(this.brandDetails._id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response) => {
      if (response.code === 0 && response.data.brand) {
        this.brandDetails = response.data.brand;
        this.stateService.updateBrandDetails(this.brandDetails);
        this.retrieveActionSummaryConfig(this.brandDetails);
        this.configuration = this.extractConfigurationFromBrandDetails(this.brandDetails);
        this.originalConfiguration = { ...this.configuration };
        this.summaryForm.patchValue({
          targetTAT: this.configuration?.targetTAT || 1
        }, { emitEvent: false });
        this.updateFormDisabledState();
      }
    });
  }

  cancelConfiguration() {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (this.isCancelling) return;

    this.isCancelling = true;

    this.accountsService.getBrandDetail(this.brandDetails._id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (response) => {
        if (response.code === 0 && response.data.brand) {
          this.brandDetails = response.data.brand;
          this.stateService.updateBrandDetails(this.brandDetails);
          this.retrieveActionSummaryConfig(this.brandDetails);
          this.configuration = this.extractConfigurationFromBrandDetails(this.brandDetails);
          this.originalConfiguration = { ...this.configuration };

          this.selectedTab = '';
          this.summaryForm.patchValue({
            actionableFromRating: '',
            actionableToRating: '',
            targetTAT: this.configuration?.targetTAT || 1,
            greenBucketLabel: '',
            greenBucketColor: '#10a754',
            redBucketLabel: '',
            redBucketColor: '#ed2424'
          }, { emitEvent: false });

          this.updateFormDisabledState();
          this.isSaveEnabled = false;
        }
        this.isCancelling = false;
      },
      error: () => {
        this.isCancelling = false;
      }
    });
  }

  private extractConfigurationFromBrandDetails(brandDetails: any): any {
    const actionManagementConfig = _.get(brandDetails, 'action_management_configuration', {});
    const widgetSettings = _.get(actionManagementConfig, 'widget_settings.action_summary', DEFAULT_ACTION_SUMMARY_COLOR_CONFIG);

    return {
      targetTAT: _.get(actionManagementConfig, 'ageing_config.turnaround_time', 1),
      widgetSettings: _.cloneDeep(widgetSettings)
    };
  }

  onTargetTatInput(input: HTMLInputElement): void {
    const raw = input.value;
    const caret = input.selectionStart ?? raw.length;
    const cleaned = this.cleanTatDraft(raw);
    if (cleaned !== raw) {
      input.value = cleaned;
      const nextCaret = Math.max(0, caret - (raw.length - cleaned.length));
      input.setSelectionRange(nextCaret, nextCaret);
    }
    this.summaryForm.get('targetTAT')?.setValue(input.value, {
      emitEvent: false,
      emitModelToViewChange: false
    });
    this.isSaveEnabled = this.hasFormChanged();
  }

  private cleanTatDraft(raw: string): string {
    const digitsAndDots = raw.replace(/[^\d.]/g, '');
    const dotIndex = digitsAndDots.indexOf('.');
    if (dotIndex === -1) {
      return digitsAndDots;
    }
    return digitsAndDots.slice(0, dotIndex + 1) + digitsAndDots.slice(dotIndex + 1).replace(/\./g, '');
  }

  canDeactivate(): Promise<boolean> | boolean {
    const hasUnsavedChanges = this.isSaveEnabled;
    if (hasUnsavedChanges) {
      return this.confirmDiscardChanges();
    }
    return true;
  }

  confirmDiscardChanges(): Promise<boolean> {
    return new Promise((resolve) => {
      if (Helpers && Helpers.SmartMessageBox) {
        Helpers.SmartMessageBox(
          {
            title: 'You have unsaved changes',
            content: 'To save your changes, please use the <b>Save</b> button. Click <b>Discard Changes</b> to lose your edits and continue. Click <b>Cancel</b> to close this popup and keep editing.',
            buttons: [
              { text: 'Cancel', type: 'secondary', key: 'Cancel' },
              { text: 'Discard Changes', type: 'primary', key: 'Discard' }
            ]
          },
          (ButtonPressed: string) => {
            setTimeout(() => {
              if (ButtonPressed === 'Discard') {
                this.isSaveEnabled = false;
                resolve(true);
              } else {
                resolve(false);
              }
            }, 50);
          }
        );
      } else {
        const shouldDiscard = confirm('You have unsaved changes. Are you sure you want to discard them?');
        if (shouldDiscard) {
          this.isSaveEnabled = false;
        }
        resolve(shouldDiscard);
      }
    });
  }
}
